import { createPortal } from "react-dom";
import { Button, Spinner, Typography } from "@/components/atoms";

export interface LoadingSplashProps {
  /** Main heading, e.g. "Connecting you to Hooli…". */
  title: string;
  /** Reassuring subtext under the heading. */
  subtitle?: string;
  /** Accessible label for the indeterminate status region. */
  statusLabel?: string;
  /** id wired to aria-labelledby so the dialog is announced by its heading. */
  headingId?: string;
  /** When provided, renders a button that calls this (e.g. "Cancel Booking"). Omit for uncancelable waits. */
  onCancel?: () => void;
  cancelLabel?: string;
}

/**
 * Full-screen dark splash for long-running waits (Figma 4106:36539). Shared presentational component
 * behind the create-booking wait (GuestBookingSplash) and the join-operator "Connecting…" step so both
 * waits look identical. Rendered through a portal to `document.body` so the `fixed inset-0` overlay
 * escapes any sticky/overflow-clipped ancestor and reliably covers the viewport.
 */
export const LoadingSplash = ({
  title,
  subtitle,
  statusLabel = "Loading",
  headingId,
  onCancel,
  cancelLabel = "Cancel",
}: LoadingSplashProps) => {
  const content = (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={headingId}
      className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-neutral-black/90 px-6 text-center"
    >
      <Typography id={headingId} variant="h3" className="text-white">
        {title}
      </Typography>
      {subtitle && (
        <Typography variant="paragraph" className="mt-2 max-w-[360px] text-white">
          {subtitle}
        </Typography>
      )}

      <div className="mt-6 flex items-center justify-center" role="status" aria-label={statusLabel}>
        <Spinner variant="light" className="mr-0 h-8 w-8" />
      </div>

      {onCancel && (
        <Button variant="primary" size="sm" className="mt-6" onClick={onCancel}>
          {cancelLabel}
        </Button>
      )}
    </div>
  );

  return typeof document === "undefined" ? content : createPortal(content, document.body);
};
